<script setup lang="ts">
import {
  Unsubscribe,
  collection,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { computed, ref } from "vue";

import CompanionCreateSubmissionChooseCurriculumModal from "~/components/Companion/CreateSubmission/ChooseCurriculumModal.vue";

const props = defineProps({
  modelValue: {
    type: String,
    required: false,
    default: undefined,
  },
  licenseExpiresLabel: {
    type: String,
    required: false,
    default: undefined,
  },
  licenseStatusLabel: {
    type: String,
    required: false,
    default: undefined,
  },
  canChangeCurriculumInline: {
    type: Boolean,
    required: false,
    default: false,
  },
  isLicenseExpired: {
    type: Boolean,
    required: false,
    default: false,
  },
  hasMatchingEligibleLicense: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue", "checklists-updated"]);

const levelsStore = useLevels();
const { allLevels } = storeToRefs(levelsStore);

const companionStore = useCompanion();
const { selectedProfile, selectedProfileId } = storeToRefs(companionStore);

const isModalOpen = ref(false);
const searchQuery = ref("");
const checklists = ref<Checklist[]>([]);
const checklistSubscription = ref<Unsubscribe | undefined>();

const selectedCurriculumLevelId = computed(() => {
  return selectedProfile.value?.curriculumLevelId;
});

const selectedCurriculum = computed(() => {
  return allLevels.value.find(
    (level) => level.id === selectedCurriculumLevelId.value
  );
});

const selectedCurriculumName = computed(() => {
  return selectedCurriculum.value?.name ?? "No curriculum selected";
});

const selectedChecklist = computed(() =>
  checklists.value.find((c) => c.id === props.modelValue)
);

const filteredChecklists = computed(() => {
  const list = checklists.value || [];

  if (!searchQuery.value) return useSortChecklists(list);
  const query = searchQuery.value.toLowerCase();
  const filtered = list.filter(
    (c) =>
      c.title?.toLowerCase().includes(query) ||
      c.unit?.toLowerCase().includes(query)
  );

  const sorted = useSortChecklists(filtered);
  return sorted;
});

const selectChecklist = (id: string) => {
  emit("update:modelValue", id);
  isModalOpen.value = false;
};

const isLoading = ref(false);

const streamProfileChecklists = () => {
  checklistSubscription.value?.();
  checklistSubscription.value = undefined;
  checklists.value = [];
  emit("checklists-updated", { checklists: [], isLoaded: false });

  if (!selectedCurriculumLevelId.value) {
    isLoading.value = false;
    emit("checklists-updated", { checklists: [], isLoaded: true });
    return;
  }

  isLoading.value = true;

  const db = useFirestore();
  const checklistsRef = collection(db, "checklists");
  const checklistsQuery = query(
    checklistsRef,
    where("levelId", "==", selectedCurriculumLevelId.value)
  );

  checklistSubscription.value = onSnapshot(
    checklistsQuery,
    (snapshot) => {
      checklists.value = snapshot.docs.map((checklistDoc) => {
        return {
          ...checklistDoc.data(),
          id: checklistDoc.id,
          path: "/checklists",
        } as Checklist;
      });
      emit("checklists-updated", {
        checklists: checklists.value,
        isLoaded: true,
      });

      isLoading.value = false;
    },
    () => {
      isLoading.value = false;
      useBaseToast("Unable to load checklists.", "error");
    }
  );
};

const openCurriculumPicker = () => {
  const { open, close } = useDialog({
    component: CompanionCreateSubmissionChooseCurriculumModal,
    props: {
      onClose: async () => {
        await close();
      },
      onSaved: async () => {
        await close();
      },
    },
  });

  open();
};

const changeCurriculum = async () => {
  openCurriculumPicker();
};

const curriculumBracketLabel = computed(() => {
  if (props.licenseStatusLabel || props.licenseExpiresLabel) return undefined;
  if (props.hasMatchingEligibleLicense) return undefined;

  return "Limited Access";
});

const curriculumStatusLabel = computed(() => {
  if (props.licenseStatusLabel) return props.licenseStatusLabel;
  if (props.licenseExpiresLabel) return `Expires ${props.licenseExpiresLabel}`;

  return undefined;
});

const curriculumDisplayName = computed(() => {
  if (!selectedCurriculumLevelId.value) return "Choose Curriculum";
  if (!curriculumBracketLabel.value) return selectedCurriculumName.value;

  return `${selectedCurriculumName.value} (${curriculumBracketLabel.value})`;
});

const shouldShowPurchaseNow = computed(() => {
  return (
    !props.licenseStatusLabel &&
    !props.licenseExpiresLabel &&
    !props.hasMatchingEligibleLicense
  );
});

onMounted(async () => {
  if (allLevels.value.length === 0) {
    await levelsStore.initializeLevels();
  }

  streamProfileChecklists();

  checkProfileCurriculum();
});

watch(selectedCurriculumLevelId, () => {
  if (
    props.modelValue &&
    selectedChecklist.value?.levelId !== selectedCurriculumLevelId.value
  ) {
    emit("update:modelValue", undefined);
  }

  streamProfileChecklists();
});

watch([selectedProfileId, selectedCurriculumLevelId], () => {
  checkProfileCurriculum();
});

onUnmounted(() => {
  checklistSubscription.value?.();
});

const lastPromptedProfileId = ref<string | undefined>();
const checkProfileCurriculum = () => {
  if (!selectedProfileId.value) {
    return;
  }

  if (selectedCurriculumLevelId.value) {
    return;
  }

  if (lastPromptedProfileId.value === selectedProfileId.value) {
    return;
  }

  lastPromptedProfileId.value = selectedProfileId.value;
  openCurriculumPicker();
};
</script>

<template>
  <div>
    <!-- Selected State Card -->
    <div
      v-if="selectedChecklist"
      class="bg-white rounded-xl border border-gray-200 p-4 shadow-sm flex items-center justify-between hover:border-primary/30 transition-colors group"
    >
      <div class="flex items-center gap-4">
        <div
          class="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center text-primary group-hover:bg-primary/20 transition-colors"
        >
          <BaseIconListChecks class="w-6 h-6" />
        </div>
        <div>
          <div
            class="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-0.5"
          >
            Checklist
          </div>
          <div class="font-bold text-gray-900">
            {{ selectedChecklist?.title }}
          </div>
          <div class="text-sm text-gray-500">
            {{ selectedChecklist?.subject }}
          </div>
        </div>
      </div>
      <BaseButton
        variant="outlined"
        size="sm"
        class=""
        @click="isModalOpen = true"
      >
        Change
      </BaseButton>
    </div>

    <!-- Empty State / Selection Trigger -->
    <div
      v-else
      class="bg-white rounded-xl border-2 border-dashed border-gray-300 p-8 flex flex-col items-center justify-center gap-3 cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-all group relative"
      @click="
        selectedCurriculumLevelId
          ? (isModalOpen = true)
          : openCurriculumPicker()
      "
    >
      <div
        class="w-14 h-14 cursor-pointer bg-gray-100 rounded-full flex items-center justify-center text-gray-400 group-hover:bg-primary/10 group-hover:text-primary transition-colors"
      >
        <BaseIconPlus class="w-8 h-8 cursor-pointer" />
      </div>
      <p
        v-if="!selectedCurriculumLevelId"
        class="text-sm font-medium text-gray-500"
      >
        Choose a curriculum to see checklists
      </p>
    </div>
    <div class="w-full mt-2 flex items-center justify-between gap-3">
      <button
        v-if="!selectedCurriculumLevelId || canChangeCurriculumInline"
        type="button"
        class="group inline-flex min-w-0 items-center gap-1.5 rounded px-1.5 py-1 text-sm font-medium text-primary underline decoration-primary/50 underline-offset-4 transition-colors hover:bg-primary/10 hover:text-primary-shade hover:decoration-primary-shade focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        @click.stop="changeCurriculum"
      >
        <span class="truncate text-left">
          {{ curriculumDisplayName }}
        </span>
        <BaseIconEdit
          class="h-3.5 w-3.5 shrink-0 transition-transform group-hover:scale-110"
        />
      </button>
      <p
        v-else
        class="min-w-0 truncate px-1.5 py-1 text-sm font-medium text-gray-700"
      >
        {{ curriculumDisplayName }}
      </p>
      <div class="ml-auto flex items-center gap-2 pr-1.5">
        <p
          v-if="curriculumStatusLabel"
          class="min-w-0 truncate py-1 text-sm font-medium"
          :class="[isLicenseExpired ? 'text-danger-default' : 'text-gray-500']"
        >
          {{ curriculumStatusLabel }}
        </p>
        <a
          v-if="shouldShowPurchaseNow"
          :href="
            selectedCurriculum?.external_purchasing_url ?? 'https://iew.com'
          "
          class="inline-flex items-center justify-center rounded px-2.5 py-1 text-xs font-medium bg-secondary text-white hover:bg-secondary-shade transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1"
        >
          Purchase Now
        </a>
      </div>
    </div>

    <!-- Selection Modal -->
    <BaseDialogComponent
      :open="isModalOpen"
      class="max-w-2xl w-full"
      @update:open="isModalOpen = $event"
    >
      <template #header>
        <div class="flex justify-between items-center w-full">
          <h2 class="text-xl font-bold text-gray-900">Select a Checklist</h2>
          <BaseIconButton variant="text" @click="isModalOpen = false">
            <BaseIconClose />
          </BaseIconButton>
        </div>
      </template>
      <template #content>
        <div class="p-6 w-[600px] max-w-full mx-auto">
          <div class="mb-6">
            <BaseSearchInput
              v-model="searchQuery"
              placeholder="Search checklists..."
              class="w-full"
            />
          </div>

          <div
            class="space-y-2 max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar"
          >
            <div v-if="isLoading" class="space-y-3">
              <div
                v-for="i in 3"
                :key="i"
                class="p-4 rounded-lg border border-gray-100 animate-pulse flex items-center gap-4"
              >
                <div class="w-10 h-10 bg-gray-100 rounded-lg" />
                <div class="flex-1 space-y-2">
                  <div class="h-4 bg-gray-100 rounded w-1/3" />
                  <div class="h-3 bg-gray-50 rounded w-1/4" />
                </div>
              </div>
            </div>

            <template v-else>
              <div
                v-if="!selectedCurriculumLevelId"
                class="text-center py-12 px-6"
              >
                <div
                  class="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4 text-gray-300"
                >
                  <BaseIconListChecks class="w-8 h-8" />
                </div>
                <h3 class="font-bold text-gray-900 mb-1">
                  Choose a curriculum
                </h3>
                <p class="text-sm text-gray-500 max-w-[260px] mx-auto">
                  Select a curriculum before choosing a checklist.
                </p>
                <BaseButton
                  variant="outlined"
                  size="sm"
                  class="mt-4"
                  @click="openCurriculumPicker"
                >
                  Choose Curriculum
                </BaseButton>
              </div>
              <div
                v-for="checklist in filteredChecklists"
                :key="checklist.id"
                class="p-4 rounded-xl border-2 cursor-pointer transition-all hover:shadow-md flex items-center justify-between group relative overflow-hidden"
                :class="
                  modelValue === checklist.id
                    ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                    : 'border-gray-100 hover:border-primary/30 hover:bg-gray-50/50'
                "
                @click="selectChecklist(checklist.id!)"
              >
                <div class="flex items-center gap-4 min-w-0 flex-1">
                  <div
                    class="w-12 h-12 rounded-lg flex-shrink-0 flex items-center justify-center transition-colors"
                    :class="
                      modelValue === checklist.id
                        ? 'bg-primary text-white'
                        : 'bg-gray-100 text-gray-400 group-hover:bg-primary/10 group-hover:text-primary'
                    "
                  >
                    <BaseIconListChecks class="w-6 h-6" />
                  </div>
                  <div class="min-w-0 flex-1">
                    <h3
                      class="font-bold text-gray-900 group-hover:text-primary transition-colors break-words whitespace-normal leading-tight"
                    >
                      {{ checklist.title }}
                    </h3>
                    <div class="flex items-center gap-2 mt-1 flex-wrap">
                      <span
                        class="text-xs font-medium px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full whitespace-nowrap"
                      >
                        {{ checklist.subject }}
                      </span>
                    </div>
                  </div>
                </div>
                <div class="flex items-center gap-3 flex-shrink-0 ml-4">
                  <div
                    v-if="modelValue === checklist.id"
                    class="w-6 h-6 bg-primary rounded-full flex items-center justify-center text-white shadow-sm"
                  >
                    <BaseIconCheck class="w-4 h-4" />
                  </div>
                  <BaseIconChevronRight
                    class="w-5 h-5 text-gray-300 group-hover:text-primary transition-colors"
                  />
                </div>
              </div>

              <div
                v-if="
                  selectedCurriculumLevelId && filteredChecklists.length === 0
                "
                class="text-center py-12 px-6"
              >
                <div
                  class="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4 text-gray-300"
                >
                  <BaseIconSearch class="w-8 h-8" />
                </div>
                <h3 class="font-bold text-gray-900 mb-1">
                  No checklists found
                </h3>
                <p class="text-sm text-gray-500 max-w-[240px] mx-auto">
                  We couldn't find any checklists matching "{{ searchQuery }}"
                </p>
                <BaseButton
                  v-if="searchQuery"
                  variant="text"
                  size="sm"
                  class="mt-2"
                  @click="searchQuery = ''"
                >
                  Clear search
                </BaseButton>
              </div>
            </template>
          </div>
        </div>
      </template>
    </BaseDialogComponent>
  </div>
</template>
